import React, { useState } from 'react';
import { Link as RouterLink, useParams, Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../../constants/documentTheme';
import { developerPath } from '../../utils/developerHost';
import { publicApiBase } from '../../utils/developerHost';
import {
  API_ENDPOINTS,
  API_SECTIONS,
  findEndpoint,
  sectionForEndpoint,
} from '../../data/developerApiReference';

const EXAMPLE_PATH_VALUES = {
  id: '1001',
  ref: 'BK-1001',
  itemId: '21',
  documentId: '6',
  tierId: '3',
  offerId: '41',
};

const methodColor = {
  GET: '#1B6B4A',
  POST: '#1F4E79',
  PATCH: '#8A5A00',
  PUT: '#8A5A00',
  DELETE: '#8B2E2E',
};

const codeSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12.5,
  lineHeight: 1.55,
  bgcolor: DOCUMENT.ivory,
  border: `1px solid ${DOCUMENT.rule}`,
  p: 2,
  overflowX: 'auto',
  whiteSpace: 'pre',
  color: DOCUMENT.ink,
  m: 0,
};

function CopyCode({ code, copyLabel, copiedLabel }) {
  const [copied, setCopied] = useState(false);
  return (
    <Box sx={{ position: 'relative', mt: 1.5 }}>
      <Box component="pre" sx={codeSx}>{code}</Box>
      <Button
        size="small"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch {
            // ignore
          }
        }}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          minWidth: 0,
          px: 1.25,
          fontSize: 12,
          textTransform: 'none',
          color: DOCUMENT.ink2,
          bgcolor: DOCUMENT.surface,
          border: `1px solid ${DOCUMENT.rule}`,
          borderRadius: 0,
        }}
      >
        {copied ? copiedLabel : copyLabel}
      </Button>
    </Box>
  );
}

function ParamsTable({ title, rows, t, showRequired = true }) {
  if (!rows?.length) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, mb: 1.25 }}>{title}</Typography>
      <Table size="small" sx={{ border: `1px solid ${DOCUMENT.ruleHard}`, bgcolor: DOCUMENT.surface }}>
        <TableHead>
          <TableRow>
            <TableCell>{t('developerPortal.apiRef.colName')}</TableCell>
            <TableCell>{t('developerPortal.apiRef.colType')}</TableCell>
            {showRequired ? (
              <TableCell>{t('developerPortal.apiRef.colRequired')}</TableCell>
            ) : null}
            <TableCell>{t('developerPortal.apiRef.colDescription')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.name}>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{row.name}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.type}</TableCell>
              {showRequired ? (
                <TableCell>{row.required ? t('developerPortal.apiRef.yes') : t('developerPortal.apiRef.no')}</TableCell>
              ) : null}
              <TableCell sx={{ color: DOCUMENT.ink2 }}>{t(row.descriptionKey)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function ErrorCodesTable({ rows, t }) {
  if (!rows?.length) return null;
  return (
    <Table size="small" sx={{ mt: 1.5, border: `1px solid ${DOCUMENT.ruleHard}`, bgcolor: DOCUMENT.surface }}>
      <TableHead>
        <TableRow>
          <TableCell>{t('developerPortal.apiRef.colStatus')}</TableCell>
          <TableCell>{t('developerPortal.apiRef.colCode')}</TableCell>
          <TableCell>{t('developerPortal.apiRef.colDescription')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.status}-${row.code}`}>
            <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{row.status}</TableCell>
            <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{row.code}</TableCell>
            <TableCell sx={{ color: DOCUMENT.ink2 }}>{t(row.descriptionKey)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function GuideBody({ text }) {
  if (!text) return null;
  return (
    <Typography
      sx={{
        color: DOCUMENT.ink2,
        fontSize: 14.5,
        lineHeight: 1.75,
        mb: 1.5,
        maxWidth: '72ch',
        whiteSpace: 'pre-line',
      }}
    >
      {text}
    </Typography>
  );
}

/** List all endpoints as horizontal cards (Cycloid-style). */
export const ApiReferenceIndex = () => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography sx={{ fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: DOCUMENT.placeholder, mb: 1 }}>
        {t('developerPortal.apiRef.eyebrow')}
      </Typography>
      <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 28, md: 36 }, mb: 1 }}>
        {t('developerPortal.apiRef.title')}
      </Typography>
      <Typography sx={{ color: DOCUMENT.ink2, mb: 3.5, maxWidth: '68ch', lineHeight: 1.65 }}>
        {t('developerPortal.apiRef.subtitle', { base: `${publicApiBase()}/v1` })}
      </Typography>

      {API_SECTIONS.map((section) => (
        <Box key={section.id} sx={{ mb: 4 }}>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 0.75 }}>
            {t(section.titleKey)}
          </Typography>
          {section.id === 'overview' ? (
            <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.6, mb: 1.75, maxWidth: '68ch' }}>
              {t('developerPortal.apiRef.overviewIntro')}
            </Typography>
          ) : (
            <Box sx={{ mb: 1.5 }} />
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {section.endpoints.map((id) => {
              const ep = API_ENDPOINTS[id];
              if (!ep) return null;
              return (
                <Paper
                  key={id}
                  elevation={0}
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    p: 2,
                    border: `1px solid ${DOCUMENT.ruleHard}`,
                    borderRadius: 0,
                    bgcolor: DOCUMENT.surface,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                      {ep.method ? (
                        <Chip
                          label={ep.method}
                          size="small"
                          sx={{
                            borderRadius: 0,
                            bgcolor: methodColor[ep.method] || DOCUMENT.oxblood,
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: 11,
                            height: 22,
                          }}
                        />
                      ) : (
                        <Chip
                          label={t('developerPortal.apiRef.guide')}
                          size="small"
                          sx={{ borderRadius: 0, bgcolor: DOCUMENT.ivory, border: `1px solid ${DOCUMENT.rule}`, height: 22 }}
                        />
                      )}
                      {ep.path ? (
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 13.5 }}>{ep.path}</Typography>
                      ) : null}
                    </Box>
                    <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 0.35 }}>
                      {t(ep.titleKey)}
                    </Typography>
                    <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14, lineHeight: 1.55, mb: ep.guideSections ? 1 : 0 }}>
                      {t(ep.summaryKey)}
                    </Typography>
                    {ep.kind === 'guide' && ep.guideSections?.length ? (
                      <Typography sx={{ color: DOCUMENT.ink3, fontSize: 12.5, lineHeight: 1.5 }}>
                        {t('developerPortal.apiRef.covers')}:{' '}
                        {ep.guideSections
                          .slice(0, 4)
                          .map((s) => t(s.titleKey))
                          .join(' · ')}
                        {ep.guideSections.length > 4 ? '…' : ''}
                      </Typography>
                    ) : null}
                  </Box>
                  <Button
                    component={RouterLink}
                    to={developerPath(`docs/api/${section.id}/${id}`)}
                    variant="contained"
                    disableElevation
                    sx={{
                      alignSelf: { xs: 'stretch', sm: 'center' },
                      borderRadius: 0,
                      textTransform: 'none',
                      bgcolor: DOCUMENT.oxblood,
                      px: 2.5,
                      whiteSpace: 'nowrap',
                      '&:hover': { bgcolor: DOCUMENT.oxblood },
                    }}
                  >
                    {t('developerPortal.apiRef.open')}
                  </Button>
                </Paper>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

/** Duffel-style per-endpoint documentation page. */
export const ApiEndpointDoc = () => {
  const { t } = useTranslation();
  const { sectionId, endpointId } = useParams();
  const endpoint = findEndpoint(endpointId);
  const section = sectionForEndpoint(endpointId);
  const apiBase = publicApiBase();
  const copyLabel = t('developerPortal.docs.copy');
  const copiedLabel = t('developerPortal.docs.copied');

  if (!endpoint || (sectionId && section && section.id !== sectionId)) {
    return <Navigate to={developerPath('docs/api')} replace />;
  }

  const tripCreateBody = JSON.stringify({
    title: 'Lisbon with the Millers',
    start_date: '2026-09-12',
    end_date: '2026-09-19',
    client_email: 'traveler@example.com',
    external_trip_id: 'crm-trip-441',
  });

  const guideCodeSamples = {
    authCurl: [
      'curl ' + apiBase + '/v1/profile \\',
      '  -H "Authorization: Bearer dvn_test_YOUR_KEY" \\',
      '  -H "Accept: application/json"',
    ].join('\n'),
    authFetch: [
      "const res = await fetch(API_BASE + '/v1/profile', {",
      '  headers: {',
      "    Authorization: 'Bearer ' + process.env.DVN_API_KEY,",
      '    Accept: "application/json",',
      '  },',
      '});',
      'if (!res.ok) {',
      '  const body = await res.json();',
      '  throw new Error(body?.error?.code || res.statusText);',
      '}',
      'const { data: profile } = await res.json();',
    ].join('\n'),
    authResponse: JSON.stringify({
      data: {
        id: 9,
        company_name: 'Coastal Voyages',
        email: 'hello@coastalvoyages.com',
        environment: 'test',
      },
    }, null, 2),
    listPackagesCurl: [
      'curl "' + apiBase + '/v1/packages" \\',
      '  -H "Authorization: Bearer dvn_test_YOUR_KEY" \\',
      '  -H "Accept: application/json"',
    ].join('\n'),
    idempotencyCurl: [
      'curl -X POST ' + apiBase + '/v1/trips \\',
      '  -H "Authorization: Bearer dvn_live_YOUR_KEY" \\',
      '  -H "Idempotency-Key: trip-create-9f3c2a1b" \\',
      '  -H "Content-Type: application/json" \\',
      "  -d '" + tripCreateBody + "'",
    ].join('\n'),
    errorHandleJs: [
      'const body = await res.json().catch(() => ({}));',
      'const code = body?.error?.code;',
      'if (res.status === 429 || code === "rate_limited") {',
      '  // backoff + retry with same Idempotency-Key on writes',
      '}',
      'if (code === "partner_feature_limit" || code === "sandbox_write_blocked") {',
      '  // prompt upgrade / switch to dvn_live_',
      '}',
      'if (code === "validation_error") {',
      '  // fix request using body.error.message',
      '}',
    ].join('\n'),
  };

  const examplePath = endpoint.path
    ? endpoint.path.replace(/\{(\w+)\}/g, (match, name) => EXAMPLE_PATH_VALUES[name] || match)
    : '';
  const authKey = endpoint.sandbox === false ? 'dvn_live_YOUR_KEY' : 'dvn_test_YOUR_KEY';
  const methodPrefix = endpoint.method && endpoint.method !== 'GET' ? ('-X ' + endpoint.method + ' ') : '';
  const curl = endpoint.method
    ? [
        'curl ' + methodPrefix + apiBase + examplePath + ' \\',
        '  -H "Authorization: Bearer ' + authKey + '"'
          + (endpoint.idempotent || endpoint.requestExample ? ' \\' : ''),
        endpoint.idempotent ? '  -H "Idempotency-Key: example-001" \\' : null,
        endpoint.requestExample ? '  -H "Content-Type: application/json" \\' : null,
        endpoint.requestExample
          ? ("  -d '" + JSON.stringify(endpoint.requestExample) + "'")
          : null,
      ].filter(Boolean).join('\n')
    : null;

  const resolveGuideCode = (guideSection) => {
    if (guideSection.code) return guideSection.code;
    if (guideSection.codeKey) return guideCodeSamples[guideSection.codeKey] || null;
    return null;
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 1.5 }}>
        <Box component={RouterLink} to={developerPath('docs/api')} sx={{ color: DOCUMENT.ink3, textDecoration: 'none' }}>
          {t('developerPortal.apiRef.title')}
        </Box>
        {' / '}
        {section ? t(section.titleKey) : ''}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mb: 1 }}>
        {endpoint.method ? (
          <Chip
            label={endpoint.method}
            sx={{
              borderRadius: 0,
              bgcolor: methodColor[endpoint.method] || DOCUMENT.oxblood,
              color: '#fff',
              fontWeight: 700,
            }}
          />
        ) : (
          <Chip
            label={t('developerPortal.apiRef.guide')}
            size="small"
            sx={{ borderRadius: 0, bgcolor: DOCUMENT.ivory, border: `1px solid ${DOCUMENT.rule}` }}
          />
        )}
        {endpoint.path ? (
          <Typography sx={{ fontFamily: 'monospace', fontSize: { xs: 16, md: 20 } }}>{endpoint.path}</Typography>
        ) : null}
      </Box>

      <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 28, md: 34 }, mb: 1 }}>
        {t(endpoint.titleKey)}
      </Typography>
      <Typography sx={{ color: DOCUMENT.ink2, fontSize: 15.5, lineHeight: 1.65, mb: 2, maxWidth: '72ch' }}>
        {t(endpoint.summaryKey)}
      </Typography>

      {section ? (
        <Box sx={{ mb: 3, pb: 2.5, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 1 }}>
            {t('developerPortal.apiRef.inThisSection')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {section.endpoints.map((id) => {
              const sibling = API_ENDPOINTS[id];
              if (!sibling) return null;
              return (
                <Button
                  key={id}
                  component={RouterLink}
                  to={developerPath(`docs/api/${section.id}/${id}`)}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    borderRadius: 0,
                    border: `1px solid ${DOCUMENT.ruleHard}`,
                    color: id === endpointId ? DOCUMENT.ivory : DOCUMENT.ink,
                    bgcolor: id === endpointId ? DOCUMENT.oxblood : DOCUMENT.surface,
                  }}
                >
                  {t(sibling.titleKey)}
                </Button>
              );
            })}
          </Box>
        </Box>
      ) : null}

      <GuideBody text={t(endpoint.bodyKey)} />

      {(endpoint.id === 'create-trip' || endpoint.id === 'create-package') ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 0 }}>
          {t('developerPortal.sandbox.apiRefHint')}
          {' '}
          <Button
            component={RouterLink}
            to={developerPath('sandbox')}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            {t('developerPortal.sandbox.apiRefCta')}
          </Button>
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {endpoint.scope ? (
          <Chip label={`${t('developerPortal.apiRef.scope')}: ${endpoint.scope}`} size="small" sx={{ borderRadius: 0 }} />
        ) : null}
        {endpoint.sandbox === false ? (
          <Chip label={t('developerPortal.apiRef.productionOnly')} size="small" color="warning" sx={{ borderRadius: 0 }} />
        ) : endpoint.method ? (
          <Chip label={t('developerPortal.apiRef.sandboxOk')} size="small" color="success" variant="outlined" sx={{ borderRadius: 0 }} />
        ) : null}
        {endpoint.idempotent ? (
          <Chip label={t('developerPortal.apiRef.idempotent')} size="small" sx={{ borderRadius: 0 }} />
        ) : null}
      </Box>

      {curl ? (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, mb: 0.5 }}>
            {t('developerPortal.apiRef.request')}
          </Typography>
          <CopyCode code={curl} copyLabel={copyLabel} copiedLabel={copiedLabel} />
        </Box>
      ) : null}

      {endpoint.requestExample ? (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, mb: 0.5 }}>
            {t('developerPortal.apiRef.requestBody')}
          </Typography>
          <CopyCode
            code={JSON.stringify(endpoint.requestExample, null, 2)}
            copyLabel={copyLabel}
            copiedLabel={copiedLabel}
          />
        </Box>
      ) : null}

      {endpoint.examples?.length ? (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, mb: 0.5 }}>
            {t(endpoint.examplesTitleKey)}
          </Typography>
          {endpoint.examples.map((example) => (
            <Box key={example.labelKey} sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14.5 }}>
                {t(example.labelKey)}
              </Typography>
              <CopyCode
                code={JSON.stringify(example.body, null, 2)}
                copyLabel={copyLabel}
                copiedLabel={copiedLabel}
              />
            </Box>
          ))}
        </Box>
      ) : null}

      {endpoint.guideSections?.map((guideSection) => {
        const code = resolveGuideCode(guideSection);
        return (
          <Box key={guideSection.titleKey} sx={{ mt: 3.5, mb: 1 }}>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
              {t(guideSection.titleKey)}
            </Typography>
            <GuideBody text={t(guideSection.bodyKey)} />
            {code ? (
              <CopyCode code={code} copyLabel={copyLabel} copiedLabel={copiedLabel} />
            ) : null}
            {guideSection.errorTable ? (
              <ErrorCodesTable rows={endpoint.errorCodes} t={t} />
            ) : null}
          </Box>
        );
      })}

      <ParamsTable title={t('developerPortal.apiRef.pathParams')} rows={endpoint.pathParams} t={t} />
      <ParamsTable title={t('developerPortal.apiRef.queryParams')} rows={endpoint.queryParams} t={t} />
      <ParamsTable title={t('developerPortal.apiRef.bodyParams')} rows={endpoint.bodyParams} t={t} />
      <ParamsTable
        title={t('developerPortal.apiRef.responseFields')}
        rows={endpoint.responseFields}
        t={t}
        showRequired={false}
      />

      {endpoint.responseNoteKey ? (
        <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14, lineHeight: 1.6, mt: 2, maxWidth: '72ch' }}>
          {t(endpoint.responseNoteKey)}
        </Typography>
      ) : null}

      {endpoint.responseExample ? (
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, mb: 0.5 }}>
            {endpoint.kind === 'guide'
              ? t('developerPortal.apiRef.examplePayload')
              : t('developerPortal.apiRef.response')}
          </Typography>
          <CopyCode
            code={JSON.stringify(endpoint.responseExample, null, 2)}
            copyLabel={copyLabel}
            copiedLabel={copiedLabel}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default ApiReferenceIndex;
